import Configuration from '@/agilis/util/Configuration'
import { mapActions, mapGetters } from 'vuex'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import FormMarkdown from '@/agilis/components/Form/FormMarkdown/FormMarkdown.vue'
import MarkdownViewer from '@/agilis/components/Form/FormMarkdown/viewer/MarkdownViewer.vue'
import { format } from '@/plugins/moment'

export default {
  name: 'TrackerDataTableItemNotes',
  mixins: [deleteMixin],
  components: {
    FormMarkdown,
    MarkdownViewer
  },

  props: {
    trackerDTO: Object
  },

  data () {
    return {
      notesArray: [],
      editDialog: false,
      title: this.$t('label.notesNew'),
      noteDTO: { id: '', createdBy: '', text: '', trackerId: '' },
      mode: 'new'
    }
  },

  mounted () {
    this.fetchNotes()
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading'
    }),

    userCanAddNote () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'note', 'post', this.trackerDTO, this.userId)
    },

    userCanEditNote () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'note', 'put', this.trackerDTO, this.userId)
    },

    userCanDeleteNote () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'note', 'delete', this.trackerDTO, this.userId)
    },

    sortCriteria () {
      const dataTableConfig = Configuration.datatable.getDatatable({
        applicationId: this.applicationId,
        trackerTypeId: this.trackerTypeId
      })
      const sortConfig = dataTableConfig.sortNotes
      switch (sortConfig) {
        case 'DESC': return 'DESC'

        default: return 'ASC' // fallback to ASC when unset or invalid
      }
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    fetchNotes () {
      this.startLoader({ id: 'tracker-notes' })
      const url = this.$axios.agilisUrlTrackerNote(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      this.$axios
        .get(url, this.$axios.agilisRequestConfig(url))
        .then((response) => {
          this.notesArray = response.data.sort((a, b) => {
            if (this.sortCriteria === 'ASC') {
              return a.updatedTime > b.updatedTime ? 1 : -1
            }
            return a.updatedTime < b.updatedTime ? 1 : -1
          })
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-notes' })
        })
    },

    handleNoteNew () {
      this.mode = 'new'
      this.title = this.$t('label.notesNew')
      this.noteDTO.id = null
      this.noteDTO.text = null
      this.noteDTO.createdBy = this.getUsername()
      this.noteDTO.trackerId = this.trackerId
      this.editDialog = true
    },

    handleNoteEdit (note) {
      this.mode = 'edit'
      this.title = this.$t('label.notesEdit')
      this.noteDTO.id = note.id
      this.noteDTO.text = note.text
      this.noteDTO.updatedBy = this.getUsername()
      this.noteDTO.trackerId = this.trackerId
      this.editDialog = true
    },

    handleNoteQuote (note) {
      this.mode = 'new'
      this.title = this.$t('label.notesNew')
      this.noteDTO.id = null
      this.noteDTO.text = this.quoteMessage(note)
      this.noteDTO.updatedBy = this.getUsername()
      this.noteDTO.trackerId = this.trackerId
      this.editDialog = true
    },

    handleNoteSave () {
      this.startLoader({ id: 'save-tracker-note' })
      if (this.mode === 'edit') {
        const url = this.$axios.agilisUrlTrackerNoteEdit(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId,
          this.noteDTO.id)
        this.$axios
          .put(url, this.noteDTO, this.$axios.agilisRequestConfig(url))
          .then((response) => {
            this.fetchNotes()
            this.$emit('componentUpdated')
          })
          .catch((error) => {
            this.notifyError(error)
          })
          .finally(() => {
            this.stopLoader({ id: 'save-tracker-note' })
            this.editDialog = false
          })
      } else {
        const url = this.$axios.agilisUrlTrackerNote(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId)
        this.$axios
          .post(url, this.noteDTO, this.$axios.agilisRequestConfig(url))
          .then((response) => {
            this.$emit('componentUpdated')
            this.fetchNotes()
          })
          .catch((error) => {
            this.notifyError(error)
          })
          .finally(() => {
            this.stopLoader({ id: 'save-tracker-note' })
            this.editDialog = false
          })
      }
    },

    handleNoteDelete (associations) {
      const userConfirmPromise = this.notifyUserConfirm({
        title: this.computedDeleteTitle,
        messages: [this.deleteMessage(this.$t('label.notes'))],
        confirm: this.deleteNo,
        cancel: this.deleteYes
      })

      userConfirmPromise.then((userResponse) => {
        if (!userResponse) {
          this.deleteNote(associations)
        }
      })
    },

    deleteNote (note) {
      const url = this.$axios.agilisUrlTrackerNoteEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        note.id)
      this.$axios
        .delete(
          url,
          {
            ...this.$axios.agilisRequestConfig(url),
            data: note
          })
        .then(() => {
          this.$emit('componentUpdated')
          this.fetchNotes()
        })
        .catch((error) => {
          this.notifyError(error)
        })
    },

    formatDateTime (dateTime) {
      const outputFormat = this.$t('format.dateTime')
      return dateTime ? format(dateTime, outputFormat) : ''
    },

    getUsername () {
      return this.$store.state.userDataStore.userId
    },

    quoteMessage (note) {
      const l1 = `*${note.updatedBy} - ${this.formatDateTime(note.updatedTime)}:*`
      const quote = note.text.split('\n')
        .map(s => `> ${s}`)

      return [
        l1,
        ...quote,
        '',
        ''
      ]
        .join('\n')
    }
  }
}
